import React, {useState, useEffect, useMemo} from 'react';
import {useAuth} from "../../helpers/AuthContext";

function IntelligentTicketResponseTextarea({
                                               ticket,
                                               name="message",
                                               placeholder="Type your response here...",
                                           }) {
    const [isAdvising, setIsAdvising] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [advice, setAdvice] = useState(null);
    const [value, setValue] = useState('');
    const {fetch} = useAuth()

    const instructions = useMemo(() => {
        return `You are an AI assistant helping to draft a response to a customer support ticket. The ticket details are as follows:
\`\`\`
${JSON.stringify(ticket, null, 2)}
\`\`\`

Use proper grammar and punctuation. Make sure there are no stray spaces before periods.
Do not offer to set up calls or screenshares. Your responses should be concise and to the point.
        `;
    }, [ticket]);

    const handleGenerateResponse = async (e) => {
        if (isGenerating) return;
        if (!ticket) {
            alert('No ticket data available to generate response.');
            return;
        }
        e.preventDefault();
        setIsGenerating(true);
        await generateResponse();
        setIsGenerating(false);
    }

    const generateResponse = async () => {
        try {
            const res = await fetch('/admin/ai/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    instructions,
                    input: [{role: 'user', content: 'Draft a professional and helpful response to the above ticket.'}],
                    model: 'gpt-5',
                    verbosity: 'low',
                    effort: 'medium',
                })
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                throw new Error(data?.error || `Request failed with status ${res.status}`);
            }
            let assistantText = '';
            const resp = data.response;
            if (resp?.output_text) {
                assistantText = resp.output_text;
            }
            if (!assistantText) {
                assistantText = 'Received response, but could not parse text. Raw:\n' + JSON.stringify(resp, null, 2);
            }
            setValue(assistantText);
        } catch (e) {
            alert('Error generating response: ' + e.message);
        }
    }

    const handleGenerateAdvice = async (e) => {
        if (isAdvising) return;
        if (!ticket) {
            alert('No ticket data available to generate advice.');
            return;
        }
        e.preventDefault();
        setIsAdvising(true);
        await generateAdvice();
        setIsAdvising(false);
    }

    const generateAdvice = async () => {
        try {
            const res = await fetch('/admin/ai/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    instructions: `You have access to the Mayhem Marketplace Help and Support knowledge base. 
Use it to provide accurate and helpful advice to the support agent. 
Focus on supplying help article links, with explanations as to their relevance and how to communicate them. 
You may also look at past tickets for context, but do not reference personal details. 
When looking at past tickets, messages from "BK" are technically canonical.
Never guess or invent details. Do not make up links or articles. If you are unsure, say so.
Do not write the response for the agent; just provide advice and resources to help them craft their own response.
Include links for relevant help articles (type=help_article). Help article link format: https://mayhem.my/help/{slug}
`,
                    input: [
                        {
                            role: 'user',
                            content: 'Please give me advice and resources to help me respond to the following ticket:\n' +
                                '```' + JSON.stringify(ticket, null, 2) + '```'
                        }
                    ],
                    model: 'gpt-5-mini',
                    verbosity: 'low',
                    effort: 'low',
                })
            });
            const data = await res.json();
            if (!res.ok || !data.ok) {
                throw new Error(data?.error || `Request failed with status ${res.status}`);
            }
            let assistantText = '';
            const resp = data.response;
            if (resp?.output_text) {
                assistantText = resp.output_text;
            }
            if (!assistantText) {
                assistantText = 'Received response, but could not parse text. Raw:\n' + JSON.stringify(resp, null, 2);
            }
            setAdvice(assistantText);
        } catch (e) {
            alert('Error generating response: ' + e.message);
        }

    };

    const styles = {
        row: {
            display: 'flex',
            flexDirection: 'row',
            gap: 20,
            flexWrap: 'wrap',
        },
        relative: {
            position: 'relative',
            flex: 2,
        },
        right: {
            flex: 1,
            fontSize: '12px',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            padding: '10px',
            border: '1px solid #ddd',

        },
        textarea: {},
        button: {
            display: 'none',
            position: 'absolute',
            top: '10px',
            right: '10px',
            textDecoration: 'none',
            fontSize: '20px',
            cursor: 'pointer',
        },
        getAdvice: {

        },
        advice: {
            fontSize: '12px',
            whiteSpace: 'pre-wrap',
            maxHeight: '400px',
            overflowY: 'auto',
        }

    }
    return (
        <div style={styles.row}>
            <div style={styles.relative}>
                <textarea
                    name={name}
                    placeholder={placeholder}
                    className="form-control"
                    rows={6}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    style={styles.textarea}
                />
                <a
                    href="#"
                    onClick={handleGenerateResponse}
                    style={styles.button}
                    title="Generate response with AI"
                ><i className={isGenerating ? "fas fa-spinner fa-spin" : "fas fa-robot"}></i></a>
            </div>
            <div style={styles.right}>
                {advice && (
                    <div style={styles.advice}>
                        {advice}
                    </div>
                )}
                {isAdvising && (
                    <div>
                        <i className="fas fa-spinner fa-spin"></i> Thinking...
                    </div>
                )}
                {!isAdvising && !advice && (
                    <div style={styles.getAdvice}>
                        <a href="#" onClick={handleGenerateAdvice}>Search knowledgebase</a>.
                    </div>
                )}
            </div>
        </div>
    )

}

export default IntelligentTicketResponseTextarea;